import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    RadioGroup,
    FormControlLabel,
    Radio,
    CircularProgress,
    Alert,
    Autocomplete,
    Chip,
    Tabs,
    Tab
} from '@mui/material';
import { userService } from '../services/userService';
import eventService, { EventDto } from '../services/eventService';
import { messageService, SendMessageRequest } from '../services/messageService';
import { UserListItem } from '../types/user';
import { API_BASE_URL } from '../utils/config';
import axios from 'axios';
import AdminSentMessages from './AdminSentMessages';
import { formatServerDate, toHourMinute } from '../utils/dateUtils';
import { useFeedback } from '../contexts/FeedbackContext';
import { requestErrorMessage, saveErrorMessage } from '../utils/saveErrors';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { isFormDirty } from '../utils/formDirty';

/**
 * [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] Az üzenetíró űrlap ÜRES alapvonala.
 * Lelet: `AdminMessages.tsx` Tárgy/Üzenet/címzettek — a Dashboard fülváltása kérdés nélkül
 * lebontotta a fület, a beírt (akár hosszú, csoportos) üzenet elveszett. A címzett-TÍPUS rádió
 * önmagában nem beírt adat, ezért nem része az összevetésnek.
 */
const EMPTY_ADMIN_MESSAGE = { subject: '', body: '', selectedUserIds: [] as string[], selectedEventId: '' };

enum RecipientType {
    Event = 'event',
    SelectedUsers = 'selectedUsers',
    AllUsers = 'allUsers'
}

const AdminMessages: React.FC = () => {
    /** 0 = küldés, 1 = elküldött üzenetek */
    const [view, setView] = useState(0);
    const [recipientType, setRecipientType] = useState<RecipientType>(RecipientType.Event);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');

    const [users, setUsers] = useState<UserListItem[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<UserListItem[]>([]);

    const [events, setEvents] = useState<EventDto[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<string>('');
    const [eventParticipants, setEventParticipants] = useState<{userId: string; userName: string; userRoleName: string}[]>([]);
    const [loadingParticipants, setLoadingParticipants] = useState(false);

    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const { showSuccess } = useFeedback();

    // A fül képernyő-szintű űrlap: a Dashboard fülváltó kapuja (`Dashboard.tsx` `confirmLeaveUnsaved`)
    // és a munkamenet-lejárat (`AuthContext`) CSAK a bejelentett forrásról tud.
    // [3. javító kör — validátor M, 2026-10-03] CSAK a LÁTHATÓ (az aktuális címzett-típushoz tartozó)
    // kiválasztás számít: a rádió váltása után a rejtett esemény/felhasználó-választás rejtett
    // „piszkos" állapotot okozott (a kapu kérdezett, holott a képernyőn nem látszott beírt adat, és a
    // küldés sem használta volna). Az érték megmarad, így visszaváltáskor újra látszik és számít.
    const isDirty = isFormDirty(EMPTY_ADMIN_MESSAGE, {
        subject,
        body,
        selectedUserIds: recipientType === RecipientType.SelectedUsers ? selectedUsers.map(u => u.userId) : [],
        selectedEventId: recipientType === RecipientType.Event ? selectedEventId : '',
    });
    useUnsavedChanges(isDirty, 'Üzenet írása');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Calculate date range for next 30 days
            const startDate = new Date();
            const endDate = new Date();
            endDate.setDate(endDate.getDate() + 30);

            const [usersData, eventsData] = await Promise.all([
                userService.getUsers(undefined, { pageNumber: 1, pageSize: 1000 }),
                eventService.getEvents({
                    startDate: startDate.toISOString().split('T')[0],
                    endDate: endDate.toISOString().split('T')[0]
                })
            ]);
            setUsers(usersData.data);
            setEvents(eventsData);
        } catch (err) {
            console.error('Error fetching data:', err);
            // [Web UX 3. csomag (D), 2026-10-04] betöltési / nem-űrlap hiba: a lejárat-tudatos `requestErrorMessage` (a fájl mentetlen-
            // védelmes, ezért a közvetlen `apiErrorMessage` itt tiltott — `unsavedCoverage.test.ts`).
            setError(requestErrorMessage(err, 'Hiba történt az adatok betöltésekor. Ellenőrizd a kapcsolatot.'));
        } finally {
            setLoading(false);
        }
    };

    const handleEventChange = async (eventId: string) => {
        setSelectedEventId(eventId);
        setEventParticipants([]);
        if (!eventId) return;

        setLoadingParticipants(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/events/${eventId}/participants`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setEventParticipants(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error loading participants:', err);
            setEventParticipants([]);
        } finally {
            setLoadingParticipants(false);
        }
    };

    const handleSend = async () => {
        setError('');

        if (!subject.trim() || !body.trim()) {
            setError('A tárgy és az üzenet megadása kötelező.');
            return;
        }

        const request: SendMessageRequest = {
            subject,
            body,
            sendToAllUsers: false
        };

        if (recipientType === RecipientType.Event) {
            if (!selectedEventId) {
                setError('Válassz ki egy eseményt!');
                return;
            }
            request.eventId = selectedEventId;
        } else if (recipientType === RecipientType.SelectedUsers) {
            if (selectedUsers.length === 0) {
                setError('Válassz ki legalább egy címzettet!');
                return;
            }
            request.recipientUserIds = selectedUsers.map(u => u.userId);
        } else if (recipientType === RecipientType.AllUsers) {
            request.sendToAllUsers = true;
        }

        setSending(true);
        try {
            const response = await messageService.sendMessage(request);
            showSuccess(response.message);
            // Reset form
            setSubject('');
            setBody('');
            setSelectedUsers([]);
            setSelectedEventId('');
            setEventParticipants([]);
        } catch (err: any) {
            // [3. javító kör, F-1] lejáratkor (újra-belépő ablak) a „megmaradtak" mondat, nem a nyers 401
            setError(saveErrorMessage(err, 'Hiba történt az üzenet küldésekor.'));
        } finally {
            setSending(false);
        }
    };

    if (loading) return <CircularProgress />;

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Üzenetek
            </Typography>

            {/* A két nézet ITT, a fülön BELÜL váltakozik. A Dashboard fülei index alapján
                dőlnek el (`activeTab === 9 && isAdmin`), és szerepkörönként más-más az
                indexük — egy új felső szintű fül mindegyiket elcsúsztatná. */}
            <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ mb: 2 }}>
                <Tab label="Küldés" />
                <Tab label="Elküldött üzenetek" />
            </Tabs>

            {view === 1 && <AdminSentMessages />}

            <Box sx={{ display: view === 0 ? 'block' : 'none' }}>
            <Paper sx={{ p: 3 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <FormControl component="fieldset" sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>Címzettek:</Typography>
                    <RadioGroup
                        row
                        value={recipientType}
                        onChange={(e) => setRecipientType(e.target.value as RecipientType)}
                    >
                        <FormControlLabel value={RecipientType.Event} control={<Radio />} label="Esemény résztvevői" />
                        <FormControlLabel value={RecipientType.SelectedUsers} control={<Radio />} label="Kiválasztott felhasználók" />
                        <FormControlLabel value={RecipientType.AllUsers} control={<Radio />} label="Minden felhasználó" />
                    </RadioGroup>
                </FormControl>

                <Box sx={{ mb: 3 }}>
                    {recipientType === RecipientType.Event && (
                        <Box>
                            <FormControl fullWidth>
                                <InputLabel id="event-select-label">Esemény kiválasztása</InputLabel>
                                <Select
                                    labelId="event-select-label"
                                    value={selectedEventId}
                                    label="Esemény kiválasztása"
                                    onChange={(e) => handleEventChange(e.target.value)}
                                >
                                    {events.map((event) => (
                                        <MenuItem key={event.eventId} value={event.eventId}>
                                            {/* [Web UX 1. csomag (5.1), 2026-10-03] locale nélküli `toLocaleDateString` + `new Date('yyyy-MM-dd')`
                                                (UTC-éjfél) + nyers `18:00:00` helyett: magyar alak, a nap zóna-tolás nélkül. */}
                                            {formatServerDate(event.eventDate)} - {event.eventName} ({toHourMinute(event.startTime)})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {loadingParticipants && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <CircularProgress size={16} />
                                    <Typography variant="body2" color="text.secondary">Résztvevők betöltése...</Typography>
                                </Box>
                            )}
                            {!loadingParticipants && selectedEventId && eventParticipants.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Címzettek ({eventParticipants.length} fő):
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {eventParticipants.map((p) => (
                                            <Chip
                                                key={p.userId}
                                                label={`${p.userName} (${p.userRoleName})`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            {!loadingParticipants && selectedEventId && eventParticipants.length === 0 && (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    Nincsenek résztvevők ehhez az eseményhez.
                                </Alert>
                            )}
                        </Box>
                    )}

                    {recipientType === RecipientType.SelectedUsers && (
                        <Autocomplete
                            multiple
                            options={users}
                            getOptionLabel={(option) => `${option.name} (${option.email})`}
                            value={selectedUsers}
                            onChange={(_, newValue) => setSelectedUsers(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Felhasználók keresése" placeholder="Kezdd el írni a nevet..." />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip label={option.name} {...getTagProps({ index })} />
                                ))
                            }
                        />
                    )}

                    {recipientType === RecipientType.AllUsers && (
                        <Alert severity="warning">
                            Figyelem! Az üzenet az összes aktív felhasználónak ki lesz küldve.
                        </Alert>
                    )}
                </Box>

                <TextField
                    fullWidth
                    label="Tárgy"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    sx={{ mb: 2 }}
                    required
                    // A tárgy a postafiókban is tárolódik (max. 200 karakter). A korlát itt
                    // azért van, hogy a küldés ne fusson hibára egy hosszú beillesztett szöveg
                    // miatt — a szerver is ellenőrzi.
                    slotProps={{ htmlInput: { maxLength: 200 } }}
                    helperText={`${subject.length}/200`}
                />

                <TextField
                    fullWidth
                    label="Üzenet szövege"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    multiline
                    rows={6}
                    sx={{ mb: 3 }}
                    required
                    helperText="HTML formázás nem támogatott, de sortörések használhatók."
                />

                <Button
                    variant="contained"
                    size="large"
                    onClick={handleSend}
                    disabled={sending}
                    startIcon={sending && <CircularProgress size={20} color="inherit" />}
                >
                    {sending ? 'Küldés folyamatban...' : 'Üzenet küldése'}
                </Button>
            </Paper>
            </Box>
        </Box>
    );
};

export default AdminMessages;
